import { ViewIds } from '@ardoq/api-types';
import {
  persistentReducedStream,
  reducer,
  type ExtractPayload,
} from '@ardoq/rxbeach';
import { updateViewProperties } from '@ardoq/view-settings';
import { distinctUntilChanged, map, type Observable } from 'rxjs';
import { isEqual, pick } from 'lodash';
import {
  isViewpointModeDependencyMapSettingsControlsDependencies,
  type ViewpointModeDependencyMapSettingsControlsDependencies,
} from 'tabview/dependencyMap/viewpointMode/settingsControls';
import {
  isViewpointModeBubbleChartSettingsControlsDependencies,
  type ViewpointModeBubbleChartSettingsControlsDependencies,
} from 'tabview/bubbleChart/viewpointMode/settingsControls';
import { isTimelineSettingsControlsDependencies } from '@ardoq/timeline2024';
import {
  AI_COMPONENT_MATRIX_2_PROPERTY_KEYS,
  isComponentMatrix2AiViewProperties,
} from 'tabview/componentMatrix/componentMatrix2/settingsDependencies';
import type { ComponentMatrix2AiViewProperties } from 'tabview/componentMatrix/componentMatrix2/types';
import { isRecord } from '@ardoq/common-helpers';
import { hasGraphItemsGroups, hasViewInstanceId } from '@ardoq/graph';
import { isFlexibleBlockDiagramViewProperties } from 'tabview/flexibleBlockDiagram/types';

export type CurrentViewPropertiesState = {
  viewProperties: Record<string, unknown>;
  viewPropertiesViewId: ViewIds;
};

type GroupsAiProperties = {
  groupLabels: Record<string, string>;
};

export const readAiComponentMatrix2ViewProperties = ({
  viewProperties,
  viewPropertiesViewId,
}: CurrentViewPropertiesState): ComponentMatrix2AiViewProperties | undefined =>
  viewPropertiesViewId === ViewIds.COMPONENT_MATRIX_2 &&
  isComponentMatrix2AiViewProperties(viewProperties)
    ? viewProperties
    : undefined;

export const readDependencyMapSettingsControlsDependencies = ({
  viewProperties,
  viewPropertiesViewId,
}: CurrentViewPropertiesState):
  | ViewpointModeDependencyMapSettingsControlsDependencies
  | undefined =>
  viewPropertiesViewId === ViewIds.DEPENDENCY_MAP_2 &&
  isViewpointModeDependencyMapSettingsControlsDependencies(viewProperties)
    ? viewProperties
    : undefined;

export const readAiBubbleChartViewProperties = ({
  viewProperties,
  viewPropertiesViewId,
}: CurrentViewPropertiesState):
  | ViewpointModeBubbleChartSettingsControlsDependencies
  | undefined =>
  viewPropertiesViewId === ViewIds.BUBBLE &&
  isViewpointModeBubbleChartSettingsControlsDependencies(viewProperties)
    ? viewProperties
    : undefined;

export const hasGroupsAiProperties = (
  viewProperties: Record<string, unknown>
): viewProperties is GroupsAiProperties => {
  const { groupLabels } = viewProperties;

  return (
    isRecord(groupLabels) &&
    Object.values(groupLabels).every(value => typeof value === 'string')
  );
};

const projectGroupsAiProperties = (
  viewProperties: Record<string, unknown>
): GroupsAiProperties | Record<string, never> => {
  if (!hasGraphItemsGroups(viewProperties)) {
    return {};
  }

  const { groups } = viewProperties;

  return {
    groupLabels: Object.fromEntries(
      [...groups.byId.entries()].map(([id, group]) => [
        id,
        // getItemLabels() is normally undefined unless a custom override exists.
        group.getItemLabels()?.mainLabel ?? group.getLabel(),
      ])
    ),
  };
};

export const projectAiViewProperties = (
  viewId: ViewIds,
  viewProperties: Record<string, unknown>
): Record<string, unknown> => {
  switch (viewId) {
    case ViewIds.MODERNIZED_BLOCK_DIAGRAM:
    case ViewIds.RELATIONSHIPS_3:
      return {
        ...(hasViewInstanceId(viewProperties)
          ? { viewInstanceId: viewProperties.viewInstanceId }
          : {}),
        ...projectGroupsAiProperties(viewProperties),
      };

    case ViewIds.COMPONENT_MATRIX_2:
      return isComponentMatrix2AiViewProperties(viewProperties)
        ? pick(viewProperties, AI_COMPONENT_MATRIX_2_PROPERTY_KEYS)
        : {};

    case ViewIds.FLEXIBLE_BLOCK_DIAGRAM:
      return {
        ...(hasViewInstanceId(viewProperties)
          ? { viewInstanceId: viewProperties.viewInstanceId }
          : {}),
        ...(isFlexibleBlockDiagramViewProperties(viewProperties)
          ? { groupLabels: viewProperties.groups }
          : {}),
      };

    case ViewIds.DEPENDENCY_MAP_2:
      return isViewpointModeDependencyMapSettingsControlsDependencies(
        viewProperties
      )
        ? viewProperties
        : {};

    case ViewIds.BUBBLE:
      return isViewpointModeBubbleChartSettingsControlsDependencies(
        viewProperties
      )
        ? viewProperties
        : {};

    case ViewIds.TIMELINE:
      return isTimelineSettingsControlsDependencies(viewProperties)
        ? viewProperties
        : {};

    default:
      return {};
  }
};

const viewPropertiesUpdated = (
  state: CurrentViewPropertiesState,
  {
    viewProperties,
    viewId: viewPropertiesViewId,
  }: ExtractPayload<typeof updateViewProperties>
): CurrentViewPropertiesState => ({
  ...state,
  viewProperties,
  viewPropertiesViewId,
});

const defaultState: CurrentViewPropertiesState = {
  viewProperties: {},
  viewPropertiesViewId: ViewIds.NONE,
};

const publishedViewProperties$ =
  persistentReducedStream<CurrentViewPropertiesState>(
    'publishedViewProperties$',
    defaultState,
    [reducer(updateViewProperties, viewPropertiesUpdated)]
  );

const toAiViewProperties = ({
  viewProperties,
  viewPropertiesViewId,
}: CurrentViewPropertiesState): CurrentViewPropertiesState => ({
  viewProperties: projectAiViewProperties(viewPropertiesViewId, viewProperties),
  viewPropertiesViewId,
});

const currentViewProperties$: Observable<CurrentViewPropertiesState> =
  publishedViewProperties$.pipe(
    map(toAiViewProperties),
    distinctUntilChanged(isEqual)
  );

export { currentViewProperties$ };
